import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import dayjs from 'dayjs';
import WarningLoading from './WarningLoading';
import RetryNotice from './RetryNotice';
import apiRequest from '../../api/request';
import { WD, FONT_SERIF } from './warningTheme';

// "History" - the last tab of Manage access: every change Finance made on the other tabs (who can see Performance Metrics, who can see CRO Workflow, who can set
// thresholds), newest first - when, who, and what in plain words ("Gave ANU TECH access to set: Refunds, Tutor Count"). Read-only; only Finance can
// open it (the screen and the server are Finance-only). A request that changed nothing leaves no line.
// Server side: warningDashboard/accessAudit.js (the rules and the sentences) and access_history.controller.js (GET /access/history).
const BASE = 'warning-dashboard/access/history';
const PAGE = 50;
const AREAS = [
  { value: '', label: 'All' },
  { value: 'pm', label: 'Performance Metrics' },
  { value: 'cro', label: 'CRO Workflow' },
  { value: 'thresholds', label: 'Thresholds' },
];
const AREA_COLOR = { pm: WD.purple, cro: WD.brown, thresholds: WD.green };
const failure = (res, fallback) => res?.message || fallback;

const AccessHistoryPanel = () => {
  const [area, setArea] = useState('');
  const [rows, setRows] = useState([]);
  const [nextBeforeId, setNextBeforeId] = useState(null); // null = nothing older
  const [tableMissing, setTableMissing] = useState(null); // the server's words when the history table is not created yet
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null); // the first page could not load
  const [moreError, setMoreError] = useState(null); // "Show older" failed - what is on screen stays
  const [reloadKey, setReloadKey] = useState(0);
  const seq = useRef(0); // only the newest request may change the screen (a quick change of filter must not show an older answer)

  const urlFor = useCallback((beforeId) => `${BASE}?limit=${PAGE}${area ? `&area=${area}` : ''}${beforeId ? `&beforeId=${beforeId}` : ''}`, [area]);

  useEffect(() => {
    const mine = ++seq.current;
    setLoading(true);
    setMoreError(null);
    // apiRequest resolves even on a failed call (src/api/request.js), so the status check is required
    apiRequest({ url: urlFor(null), method: 'get' })
      .then((res) => {
        if (mine !== seq.current) return;
        if (res?.status !== 'success') { setError(failure(res, 'Could not load the access history')); setRows([]); return; }
        setRows(res.data?.rows || []);
        setNextBeforeId(res.data?.nextBeforeId || null);
        setTableMissing(res.data?.tableMissing ? (res.data.tableMissingMessage || 'The access history table is not created in this database yet.') : null);
        setError(null);
      })
      .catch((err) => { if (mine === seq.current) setError(err?.response?.data?.message || 'Could not load the access history'); })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  }, [urlFor, reloadKey]);

  const showOlder = () => {
    const mine = seq.current;
    setLoadingMore(true);
    setMoreError(null);
    apiRequest({ url: urlFor(nextBeforeId), method: 'get' })
      .then((res) => {
        if (mine !== seq.current) return;
        if (res?.status !== 'success') { setMoreError(failure(res, 'Could not load older changes')); return; }
        setRows((before) => [...before, ...(res.data?.rows || [])]);
        setNextBeforeId(res.data?.nextBeforeId || null);
      })
      .catch((err) => { if (mine === seq.current) setMoreError(err?.response?.data?.message || 'Could not load older changes'); })
      .finally(() => { if (mine === seq.current) setLoadingMore(false); });
  };

  return (
    <>
      <Typography component="h2" sx={{ fontFamily: FONT_SERIF, fontSize: 22, fontWeight: 600, color: WD.ink, mb: 0.5 }}>History of access changes</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 760 }}>
        Every change made on the other tabs, newest first. Only Finance can see this.
      </Typography>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={area}
        onChange={(e, next) => { if (next !== null) setArea(next); }}
        aria-label="Show"
        sx={{ mb: 2 }}
      >
        {AREAS.map((a) => <ToggleButton key={a.value || 'all'} value={a.value} sx={{ textTransform: 'none', px: 2 }}>{a.label}</ToggleButton>)}
      </ToggleButtonGroup>

      {loading && <WarningLoading />}
      {!loading && error && <RetryNotice message={error} onRetry={() => setReloadKey((k) => k + 1)} />}
      {!loading && !error && tableMissing && <Alert severity="warning" sx={{ mb: 2 }}>{tableMissing}</Alert>}

      {!loading && !error && !tableMissing && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {area ? 'No changes recorded for this part of Manage access yet.' : 'No changes to access have been recorded yet.'}
        </Typography>
      )}

      {!loading && !error && rows.length > 0 && (
        <TableContainer sx={{ maxWidth: 1100 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#FAF8F3' }}>
                <TableCell><strong>When</strong></TableCell>
                <TableCell><strong>Who</strong></TableCell>
                <TableCell><strong>What</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.at && dayjs(r.at).isValid() ? dayjs(r.at).format('D MMM YYYY, HH:mm') : '-'}</TableCell>
                  <TableCell>{r.doneBy || '-'}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <Chip size="small" variant="outlined" label={r.areaLabel || 'Other'} sx={{ color: AREA_COLOR[r.area] || WD.inkMuted, borderColor: AREA_COLOR[r.area] || WD.border }} />
                      <span>{r.summary}</span>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && !error && nextBeforeId && (
        <Box sx={{ mt: 2 }}>
          <Button variant="outlined" disabled={loadingMore} onClick={showOlder}>{loadingMore ? 'Loading...' : 'Show older'}</Button>
        </Box>
      )}
      {moreError && <Alert severity="error" sx={{ mt: 2, maxWidth: 560 }}>{moreError}</Alert>}
    </>
  );
};

export default AccessHistoryPanel;
